import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHistory } from 'react-router';
import { ELEVATE_SEARCH_ORIGINS, useElevate } from '../ElevateContext';
import { ElevateGrid } from '../ElevateGrid';
import { useTracking } from '../../../utils/tracking/useTracking';
import { styled } from 'linaria/react';
import { ElevateAutoCorrect } from './types/ElevateAutoCorrect';
import { PAGE_TYPES } from '../../../utils/tracking';

import { useProductVisibilityTracking } from '../../../utils/tracking/useProductVisibilityTracking';
import t from '@jetshop/intl';
import { ElevatePhraseSuggestions } from './types/ElevatePhraseSuggestions';
import { theme } from '../../Theme';
import { cx } from 'linaria';
import { Link } from 'react-router-dom';

const SearchPageTabContent = styled('ul')`
  margin: 0 auto;
  padding: 0px 12px;
  max-width: 90rem;
  display: flex;
  flex-direction: column;

  li {
    max-width: 600px;
    list-style: none;
    padding: 12px 2rem;
    border-bottom: 1px solid #c1c8c455;
    background-color: #fff;
    font-size: 16px;
    color: #05241e;
    font-weight: 400;
    a {
      color: inherit;
      text-decoration: none;
      font-weight: inherit;
    }

    ${theme.below.md} {
      padding: 12px 1rem 12px 0px;
    }
  }
`;

const SearchPageHeader = styled('div')`
  margin: 0 auto;
  max-width: 90rem;
  padding: 50px 12px 20px 12px;
  display: flex;
  flex-direction: column;
  gap: 30px;

  ${theme.below.lg} {
    padding: 25px 12px 16px 12px;
  }

  &.secondary-results-header {
    padding: 16px 12px;
  }

  .results-info-container {
    > * {
      margin: 0;
    }
    h1 {
      font-size: 32px;
      font-weight: 400;
      line-height: 112%;
      span {
        font-weight: 700;
        text-transform: capitalize;
      }

      ${theme.below.lg} {
        font-size: 24px;
      }
    }

    p {
      margin-top: 10px;
    }
  }
`;

const ElevateSearchPageContentListsWrapper = styled('div')`
  display: flex;
  flex-wrap: wrap;

  ${theme.below.md} {
    row-gap: 12px;
  }

  .elevate-search-page-content-list-type-button {
    padding: 12px 2rem;
    border: none;
    border-bottom: 1px solid #c1c8c4;
    background-color: #fff;
    font-size: 16px;
    color: #05241e;

    ${theme.below.md} {
      padding: 12px 8px 12px 0px;
    }

    &.active {
      border-bottom: 2px solid #05241e;
      font-weight: 700;
    }
  }
`;

const ElevateSearchPage = () => {
  const history = useHistory();
  const {
    getSearchPageData,
    queryStringToObject,
    groupContentListByType,
    notification,
    NOTIFICATION_TYPES
  } = useElevate();
  const {
    trackSearchResultClick,
    trackSearch,
    trackPageView,
    trackSelectItem
  } = useTracking();
  const params = queryStringToObject(history?.location?.search);
  const { term, ...restParams } = params;
  const [activeTab, setActiveTab] = useState(null);
  // const [searchData, setSearchData] = useState({ term, params: restParams });
  const searchTermRef = useRef('');
  const [searchPageState, setSearchPageState] = useState({
    productGroups: null,
    totalHits: 0,
    facets: [],
    sortOrder: {},
    listName: '',
    contentLists: []
  });

  const { trackProductVisibility, resetTracking } =
    useProductVisibilityTracking({
      listName: searchPageState?.listName || `Search: ${term || ''}`,
      listId: 'search',
      debounceMs: 300,
      threshold: 0.5
    });

  const updatePageData = response => {
    setSearchPageState({
      productGroups: response?.primaryList?.productGroups ?? [],
      secondaryProductGroups: response?.secondaryList?.productGroups ?? [],
      totalHits: response?.primaryList?.totalHits ?? 0,
      facets: response?.primaryList?.facets ?? [],
      sortOrder: response?.primaryList?.sort ?? {},
      listName: response?.seo?.pageHeading ?? '',
      contentLists: response?.contentLists ?? [],
      searchTerm: response?.q,
      didYouMean: response?.didYouMean,
      relatedSearches: response?.relatedSearches,
      autoCorrect: response?.autoCorrect
    });
  };

  // Reset before cards register (useLayoutEffect runs before child layout effects)
  useLayoutEffect(() => {
    resetTracking();
  }, [searchPageState.productGroups, history.location.search, resetTracking]);

  useEffect(() => {
    const params = queryStringToObject(history?.location?.search);
    // Remove 'term' from params to avoid passing it along as part of other params
    const { term, ...restParams } = params;

    const fetchDataWithFacets = async () => {
      const response = await getSearchPageData({
        searchTerm: term,
        ...restParams
      });
      searchTermRef.current = term;
      updatePageData(response);
      setActiveTab(null);

      trackSearch({
        searchTerm: term,
        results: response?.primaryList?.totalHits || 0,
        placement: 'Deep search'
      });
      trackPageView({
        pageType: PAGE_TYPES.SEARCH,
        pagePath: history?.location?.pathname,
        pageTitle:
          `Search: ${term}` ||
          (typeof document !== 'undefined' ? document.title : '')
      });
    };
    fetchDataWithFacets();
  }, [history.location.search]);

  const handleAutoCorrectClicked = originalTerm => {
    history.push({
      pathname: history.location.pathname,
      search: `term=${originalTerm}&origin=${ELEVATE_SEARCH_ORIGINS.undoAutoCorrect}`
    });
  };

  const groupedContent = groupContentListByType(searchPageState?.contentLists);

  const handleRelatedSearchClicked = (relatedSearchTerm, origin) => {
    history.push({
      pathname: history.location.pathname,
      search: `term=${relatedSearchTerm}&origin=${ELEVATE_SEARCH_ORIGINS.relatedSearches}`
    });
  };

  return (
    <div>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <SearchPageHeader>
        <div className="results-info-container">
          <h1>
            {t('Search results for')}
            <span style={{ fontWeight: 600 }}>
              "{searchPageState?.autoCorrect?.q || searchPageState?.searchTerm}"
            </span>
          </h1>
          <ElevateAutoCorrect
            autoCorrect={searchPageState?.autoCorrect}
            term={searchPageState?.searchTerm}
            clickCallback={handleAutoCorrectClicked}
          />

          {searchPageState.totalHits > 0 ? (
            <p style={{ marginTop: '10px' }}>
              {t.rich(
                'We have found {totalHits} products that match your search.',
                {
                  totalHits: <strong>{searchPageState.totalHits}</strong>
                }
              )}
            </p>
          ) : (
            <>
              {searchPageState?.secondaryProductGroups?.length > 0 && (
                <p>
                  {t(
                    'The search did not find any results, but we found some related products:'
                  )}
                </p>
              )}
            </>
          )}
        </div>

        {searchPageState?.relatedSearches?.length > 0 && (
          <ElevatePhraseSuggestions
            title={'Did you mean'}
            suggestions={searchPageState?.relatedSearches}
            clickCallback={handleRelatedSearchClicked}
          />
        )}
        <ElevateSearchPageContentListsWrapper>
          <button
            className={cx(
              `elevate-search-page-content-list-type-button`,
              activeTab === null ? 'active' : ''
            )}
            onClick={() => {
              setActiveTab(null);
            }}
          >
            {t('Products')}&nbsp;<span>({searchPageState?.totalHits})</span>
          </button>
          {Object.keys(groupedContent).map((type, index) => {
            return (
              <button
                className={cx(
                  `elevate-search-page-content-list-type-button`,
                  activeTab === type ? 'active' : ''
                )}
                key={`${type}-${index}`}
                onClick={() => {
                  setActiveTab(type);
                }}
              >
                {t(type)}&nbsp;
                <span>({groupedContent?.[type]?.length})</span>
              </button>
            );
          })}
        </ElevateSearchPageContentListsWrapper>
      </SearchPageHeader>

      {activeTab === null ? (
        <ElevateGrid
          page={'search'}
          pageData={searchPageState}
          clickCallback={(product, index) => {
            notification(NOTIFICATION_TYPES.click, product?.ticket);
            trackSearchResultClick({
              searchTerm: searchTermRef.current,
              results: searchPageState?.totalHits || 0,
              clickedTitle: product?.title,
              clickedUrl: product?.link,
              position: (index ?? 0) + 1, // 1-based position
              placement: 'Deep search'
            });

            // Also track select_item for list tracking
            if (searchPageState?.listName) {
              trackSelectItem({
                product,
                listName: searchPageState?.listName,
                index: index ?? 0
              });
            }
          }}
          inViewCallback={trackProductVisibility}
        />
      ) : (
        <SearchPageTabContent>
          {groupedContent?.[activeTab]?.map((item, index) => {
            return (
              <li key={item?.ticket}>
                <Link to={item?.link}>{item?.title}</Link>
              </li>
            );
          })}
        </SearchPageTabContent>
      )}

      {searchPageState?.secondaryProductGroups?.length > 0 && (
        <div>
          <SearchPageHeader className="secondary-results-header">
            <div className="results-info-container">
              <h2>{t('Similar search results')}</h2>
            </div>
          </SearchPageHeader>
          <ElevateGrid
            pageData={{
              productGroups: searchPageState?.secondaryProductGroups,
              totalHits: 0,
              facets: [],
              sortOrder: {},
              listName: searchPageState?.listName + ' (secondary)'
            }}
            clickCallback={(product, index) => {
              notification(NOTIFICATION_TYPES.click, product?.ticket);
              trackSearchResultClick({
                searchTerm: searchTermRef.current,
                results: searchPageState?.totalHits || 0,
                clickedTitle: product?.title,
                clickedUrl: product?.link,
                position: (index ?? 0) + 1, // 1-based position
                placement: 'Deep search'
              });
              // Also track select_item for list tracking
              if (searchPageState?.listName) {
                trackSelectItem({
                  product,
                  listName: searchPageState?.listName,
                  index: index ?? 0
                });
              }
            }}
            inViewCallback={trackProductVisibility}
          />
        </div>
      )}

      <div style={{ height: '40px' }} />
    </div>
  );
};

export default ElevateSearchPage;
